import { useState, Fragment, useRef, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
// import { Field } from "formik";
// import { ArrowDown, ArrowUp, CalendarDays, Snowflake, Zap } from "lucide-react";
import LoadingOverlay from "../../helper-components/overlay-loading";
import errorToast from "../../helper-components/error-toast";
import CollapsibleFilter from "../../helper-components/collapsible-box";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch } from "react-redux";
import { binLocationListAction } from "../../../redux/actions/general-action";
import successToast from "../../helper-components/success-toast";
import { purchaseExpiryBinAction } from "../../../redux/actions/purchase-action";
import { useFormikContext } from "formik";

const PurchaseReturnSidePanel = ({ open, setOpen }) => {
  const [loading, setLoading] = useState(false);
  const close=useRef(null);
  const headerRefs = useRef([]);
  const dispatch = useDispatch();
  const { values, setFieldValue } = useFormikContext();
  const [binOptions, setBinOptions] = useState([]);
  const [selectedBin, setSelectedBin] = useState("");
  const [dateModeDays, setDateModeDays] = useState(true); // true => Days, false => Calendar
  const [ageDays, setAgeDays] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
//   useHotkeys("1,2", (_, handler) => {
//     const key = handler.keys.join(""); 
//     const match = key.match(/^[12]$/);
//     if (!match) return;
//     const index = Number(match[0]) - 1;
//     headerRefs.current[index]?.click();
//   });
  useHotkeys("1", () => {
    headerRefs.current[0]?.click();
  });
  useHotkeys("2", () => {
    headerRefs.current[1]?.click();
  });
  useHotkeys("esc", () => {
        if (close.current) {
            close.current.click();
        }
    });

  // Validate cmr_id and load bins on open
  useEffect(() => {
    if (open) {
      if (!values?.cmr_id) {
        errorToast("Please select a vendor before loading items");
        setOpen(false);
        return;
      }
      dispatch(
        binLocationListAction(
          (res) => {
            const opts =
              res?.data?.map((b) => ({
                label: b?.bin_location_no,
                value: b?.bin_no_id,
                raw: b,
              })) ?? [];
            setBinOptions(opts);
          },
          () => {}
        )
      );
    } else {
      setSelectedBin("");
      setAgeDays("");
      setFromDate("");
      setToDate("");
      setDateModeDays(true);
    }
  }, [open, dispatch, setOpen, values?.cmr_id]);

  const applyItemsToForm = (apiItems) => {
    const transformed = (apiItems || []).map((it, idx) => ({
      sl_no: idx + 1,
      item_name: it?.item_name,
      item_generic_name: it?.item_generic_name ?? "",
      item_code: it?.item_code,
      item_hsn: it?.item_hsn,
      item_manufacturer_name: it?.item_manufacturer_name ?? "",
      item_uom: it?.item_uom,
      item_batches:
        (it?.itembatchdata || []).map((b) => ({
          item_batch_number: b?.item_batch_number,
          item_sellable_quantity: Number(b?.item_sellable_quantity) || 0,
          item_batch_free_quantity: Number(b?.item_batch_free_quantity) || 0,
          item_batch_total_effective_qauntity:
            Number(b?.item_batch_total_effective_qauntity) || 0,
          item_batch_total_base_purchase_rate:
            Number(b?.item_batch_total_base_purchase_rate) || 0,
          item_batch_for_qauntity: Number(b?.item_batch_for_qauntity) || 0,
          item_batch_for_free_qauntity:
            Number(b?.item_batch_for_free_qauntity) || 0,
          item_batch_scheme_discount_percentage:
            Number(b?.item_batch_scheme_discount_percentage) || 0,
          item_batch_scheme_discount_amount:
            Number(b?.item_batch_scheme_discount_amount) || 0,
          item_batch_scheme_purchase_rate:
            Number(b?.item_batch_scheme_purchase_rate) || 0,
          item_batch_discount_percentage:
            Number(b?.item_batch_discount_percentage) || 0,
          item_batch_gen_discount_amount:
            Number(b?.item_batch_gen_discount_amount) || 0,
          item_batch_discounted_purchase_rate:
            Number(b?.item_batch_discounted_purchase_rate) || 0,
          item_batch_purchase_rate: Number(b?.item_batch_purchase_rate) || 0,
          item_batch_discount_amount: Number(b?.item_batch_discount_amount) || 0,
          item_batch_tax_percentage: Number(b?.item_batch_tax_percentage) || 0,
          item_batch_tax_amount: Number(b?.item_batch_tax_amount) || 0,
          item_batch_final_purchase_rate:
            Number(b?.item_batch_final_purchase_rate) || 0,
          item_batch_total_purchase_rate:
            Number(b?.item_batch_total_purchase_rate) || 0,
          item_batch_unit_price: Number(b?.item_batch_unit_price) || 0,
          item_exp_date: b?.item_exp_date,
          item_id: b?.item_id,
          to_bin_location: b?.to_bin_location,
          to_bin_id: b?.to_bin_id,
        })) || [],
      item_return_open_quantity: 0,
      quantity: Number(it?.item_quantity) || 0,
      item_pack_size: 0,
      base_quantity: Number(it?.base_quantity) || 0,
      item_free_quantity: Number(it?.item_free_quantity) || 0,
      item_discount_amount: Number(it?.item_discount_amount) || 0,
      item_tax_amount: Number(it?.item_tax_amount) || 0,
      item_total_amount: Number(it?.item_total_amount) || 0,
      item_id: it?.item_id,
      pot_id: it?.pot_id ?? null,
      gort_id: it?.gort_id ?? null,
      item_gst: Number(it?.item_gst) || 0,
      item_sgst: Number(it?.item_sgst) || 0,
      item_cgst: Number(it?.item_cgst) || 0,
      item_uom_id: it?.item_uom_id,
      item_manufacturer_id: it?.item_manufacturer_id ?? "",
    }));
    const tail =
      Array.isArray(values?.data) && values.data.length > 0
        ? [values.data[values.data.length - 1]]
        : [];
    setFieldValue("data", [...transformed, ...tail]);
    const totals = transformed.reduce(
      (acc, it) => {
        acc.subTotal += Number(it.item_total_amount) || 0;
        acc.tax += Number(it.item_tax_amount) || 0;
        acc.discount += Number(it.item_discount_amount) || 0;
        return acc;
      },
      { subTotal: 0, tax: 0, discount: 0 }
    );
    setFieldValue("prt_sub_total", totals.subTotal);
    setFieldValue("prt_total_gst", totals.tax);
    setFieldValue("prt_total_discount", totals.discount);
    setFieldValue("prt_total_amount", totals.subTotal);
  };

  const handleLoad = () => {
    if (!selectedBin) {
      errorToast("Please select a bin location");
      return;
    }
    if (!values?.cmr_id) {
      errorToast("Please select a vendor");
      return;
    }
    const body = dateModeDays
      ? {
          cmr_id: values?.cmr_id,
          bin_id: selectedBin,
          age: Number(ageDays || 0),
        }
      : {
          cmr_id: values?.cmr_id,
          bin_id: selectedBin,
          from: fromDate,
          to: toDate,
        };
    if (dateModeDays && (!ageDays || Number(ageDays) <= 0)) {
      errorToast("Please enter product age (days)");
      return;
    }
    if (!dateModeDays && (!fromDate || !toDate)) {
      errorToast("Please select both From date and To date");
      return;
    }
    setLoading(true);
    dispatch(
      purchaseExpiryBinAction(
        body,
        (resp) => {
          setLoading(false);
          successToast(resp?.message || "Items loaded");
          applyItemsToForm(resp?.data?.itemData || []);
          // keep panel open for further loads
        },
        (err) => {
          setLoading(false);
          errorToast(err?.data?.message || err?.message || "Failed to load");
        }
      )
    );
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => setOpen(false)}
      >
        <div className="fixed inset-0 overflow-hidden">
          {/* Background Overlay */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
          </Transition.Child>

          {/* Slide-in Panel */}
          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="h-full relative w-[400px] sm:w-[600px] bg-white rounded-tl-lg rounded-bl-lg shadow-xl flex flex-col">
                
                {/* Header */}
                <div className="p-4 flex justify-between items-center border-b border-[var(--button-border-color)]">
                  <div>
                    <h2 className="text-lg font-semibold">Select Return Type</h2>
                    <div className="text-sm pt-1 text-[var(--description-color)]">
                      Select option from Below List
                    </div>
                  </div>
                  <button
                    ref={close}
                    onClick={() => setOpen(false)}
                  >
                    ✕
                  </button>
                </div>

                {/* Scrollable Content */}
                <div className="space-y-4 p-4 flex-1 overflow-y-auto">
                    <CollapsibleFilter
                        headerText="Load Bin Location"
                        className="w-full max-w-full"
                        headerButtonRef={(el) => (headerRefs.current[0] = el)}
                    >
                      <div className="flex flex-col gap-4">
                        <div>
                          <label className="block text-sm font-medium text-[var(--nav-heading-color)]">
                            Select Bin Loaction <span className="text-F51D00">*</span>
                          </label>
                          <select
                            className="mt-1 w-full border border-[var(--button-border-color)] rounded-md px-3 py-2 text-sm bg-white"
                            value={selectedBin}
                            onChange={(e) => setSelectedBin(e.target.value)}
                          >
                            <option value="">Select Bin</option>
                            {binOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-[var(--nav-heading-color)]">
                            Select Date Format
                          </span>
                          <div className="flex items-center gap-2 text-sm text-[var(--description-color)]">
                            <span>Calendar</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={dateModeDays}
                                onChange={(e) => setDateModeDays(e.target.checked)}
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-primary transition-colors"></div>
                              <span className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></span>
                            </label>
                            <span className="font-semibold">Days</span>
                          </div>
                        </div>
                        {dateModeDays ? (
                          <div>
                            <label className="block text-sm font-medium text-[var(--nav-heading-color)]">
                              Product Age
                            </label>
                            <input
                              type="number"
                              className="mt-1 w-full border border-[var(--button-border-color)] rounded-md px-3 py-2 text-sm bg-white"
                              placeholder="Enter Product Age"
                              value={ageDays}
                              onChange={(e) => setAgeDays(e.target.value)}
                              min={1}
                            />
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-[var(--nav-heading-color)]">
                                From Date
                              </label>
                              <input
                                type="date"
                                className="mt-1 w-full border border-[var(--button-border-color)] rounded-md px-3 py-2 text-sm bg-white"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-[var(--nav-heading-color)]">
                                To Date
                              </label>
                              <input
                                type="date"
                                className="mt-1 w-full border border-[var(--button-border-color)] rounded-md px-3 py-2 text-sm bg-white"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                              />
                            </div>
                          </div>
                        )}
                        <div className="flex justify-end">
                          <button
                            type="button"
                            className="bg-primary text-white px-5 py-2 rounded-md text-sm"
                            onClick={handleLoad}
                          >
                            Load
                          </button>
                        </div>
                      </div>
                    </CollapsibleFilter>
                    <CollapsibleFilter
                        headerText="Load items based on the movement"
                        className="w-full max-w-full"
                        headerButtonRef={(el) => (headerRefs.current[1] = el)}
                    >
                        <div className="flex items-center justify-between mb-3">
                            <span className="font-medium text-gray-700">Select Date Format</span>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span>Calendar</span>
                            </div>
                        </div>
                    </CollapsibleFilter>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-[var(--button-border-color)] bg-white sticky bottom-0 flex justify-between">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="bg-[#ff0000] text-white px-6 py-2 rounded-md fw_600 hover:opacity-90"
                  >
                    Cancel
                  </button>
                  <div />
                </div>

                {/* Loading Overlay */}
                <LoadingOverlay isLoading={loading} />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default PurchaseReturnSidePanel;
